import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { type Muted, moderation } from "./api";
import { CustomerDetails } from "./CustomerDetails";
import type { PanelComment, RoomChat } from "./useRoomChat";
import styles from "./Chat.module.scss";

/**
 * Comments, on the show page (M0.10, spec §10.2, §13.2, §17.4).
 *
 * Two things at once, and they are the two halves of spec §13.2's *powers, and their limits*:
 * the feed a moderator watches, with a delete and a mute against every comment, and the list of
 * who is currently muted, with a way to lift it.
 *
 * ## Every action goes to the API, not to the room
 *
 * The panel holds a chat token with `SEND_MESSAGE` and nothing else — no `DELETE_MESSAGE`, no
 * `DISCONNECT_USER`, even for an operator. So each control here is an HTTP request that is gated,
 * attributed and recorded (`R-MOD-2`), and the room updates for everybody through IVS's own event.
 * `packages/functions/src/show-chat.ts` and `chatToken` in `packages/core/src/chat/rooms.ts` both
 * carry the argument; the short version is that a capability in a token is a way to moderate with
 * no record of who did it.
 *
 * ## Live for the feed, ended for the mute list
 *
 * There is no room before a show and none after it (spec §10.1.1, `R-END-2`), so there is nothing
 * to moderate and nothing to watch. The mute list is still worth reading afterwards — it is the
 * record of what was done — and `ShowPage` is what decides; this component renders whatever it is
 * given.
 *
 * ## What is not here
 *
 * **Slow mode** (`R-MOD-6`, `R-HOST-23`) and the **report queue** (`R-MOD-8` to `R-MOD-10`,
 * `R-HOST-22`), both deferred by the implementation plan to M3.2, and named there rather than
 * left to be discovered missing.
 */
export function ChatPanel({
  showId,
  live,
  chat,
}: {
  showId: string;
  live: boolean;
  /**
   * The show page's connection to the room (`useRoomChat`), shared with presenter mode so that
   * opening it does not join the room a second time from the same browser.
   */
  chat: RoomChat;
}) {
  const { t } = useTranslation();
  const [mutes, setMutes] = useState<Muted[]>([]);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  /** The account whose details are open (`R-MOD-1b`), or none. */
  const [inspecting, setInspecting] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      setMutes((await moderation.read(showId)).mutes);
    } catch {
      // Silent: the mute list is context beside a feed that is working. A failure to read it is
      // not worth an alert over a live show, and the next poll fixes it.
    }
  }, [showId]);

  useEffect(() => {
    let alive = true;
    void moderation.read(showId).then(
      (overview) => {
        if (alive) setMutes(overview.mutes);
      },
      () => undefined,
    );
    // Polled rather than pushed, like everything else on this page: two people moderate one show
    // (`R-HOST-31`), so a mute imposed by the other one has to appear here without a reload. The
    // panel subscribes to no AppSync channel — see `shows/poll.ts` for why — so this stays a poll.
    const timer = setInterval(() => void load(), 10_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [showId, load]);

  /** One place where an action runs, its failure is made showable, and `busy` is cleared. */
  async function act(key: string, work: () => Promise<string | undefined>) {
    setBusy(key);
    setProblem(undefined);
    setNote(undefined);
    try {
      setNote(await work());
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : t("chat.unreachable"));
    } finally {
      setBusy(undefined);
      await load();
    }
  }

  const onDelete = (comment: PanelComment) =>
    act(`delete:${comment.id}`, async () => {
      await moderation.remove(showId, comment.id, comment.userId);
      // Removed locally as well as at IVS. The room's own delete event does the same thing a
      // moment later and `forget` is idempotent — but a moderator who has just pressed delete
      // should watch it go, not wait for a round trip through a WebSocket.
      chat.forget(comment.id);
      return undefined;
    });

  const onMute = (comment: PanelComment) =>
    act(`mute:${comment.userId}`, async () => {
      const outcome = await moderation.mute(showId, comment.userId);
      // Reported rather than assumed: without the disconnect the mute waits for their token to
      // expire, which is up to three hours. See `packages/core/src/chat/mutes.ts`.
      return outcome.disconnected
        ? t("chat.mutedNote", { name: comment.name || t("chat.someone") })
        : t("chat.mutedSlowNote", { name: comment.name || t("chat.someone") });
    });

  const onUnmute = (accountId: string, name: string) =>
    act(`unmute:${accountId}`, async () => {
      await moderation.unmute(showId, accountId);
      return t("chat.unmutedNote", { name });
    });

  return (
    <Paper withBorder radius="lg" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text fw={650}>{t("chat.heading")}</Text>
          {live ? (
            <Badge
              size="sm"
              variant="light"
              color={chat.connected ? "green" : "orange"}
            >
              {chat.connected ? t("chat.connected") : t("chat.connecting")}
            </Badge>
          ) : null}
        </Group>

        {problem ? (
          <Alert
            color="red"
            variant="light"
            radius="md"
            withCloseButton
            onClose={() => setProblem(undefined)}
          >
            {problem}
          </Alert>
        ) : null}
        {note ? (
          <Alert color="green" variant="light" radius="md">
            {note}
          </Alert>
        ) : null}

        {live ? (
          <>
            <div className={styles.feed}>
              {chat.comments.length === 0 ? (
                /*
                  **An empty feed on a busy show is the ordinary case after a reload**, and
                  *no comments yet* alone would be wrong: IVS Chat retains nothing (`feed.ts`), so
                  this panel can only show what has arrived since it connected. A moderator who
                  refreshed mid-show needs to know that what they are looking at is a fresh start
                  rather than a quiet room — the difference decides whether they go looking for a
                  fault.
                */
                <Stack gap={2}>
                  <Text size="sm" c="dimmed">
                    {chat.connected ? t("chat.empty") : t("chat.emptyConnecting")}
                  </Text>
                  {chat.connected ? (
                    <Text size="xs" c="dimmed">
                      {t("chat.emptyHistory")}
                    </Text>
                  ) : null}
                </Stack>
              ) : (
                /*
                  Newest **first in the DOM**, because the container is `column-reverse` — which
                  displays them bottom-up, so the newest is at the bottom where it belongs. The
                  reason for the trick rather than the customer feed's explicit auto-scroll: a
                  `column-reverse` container that is already at its end has `scrollTop` of zero
                  and stays there as messages arrive, so following the feed needs no scroll
                  handling and reading back still works exactly as a moderator expects.
                */
                [...chat.comments]
                  .reverse()
                  .map((comment) => (
                    <Line
                      key={comment.id}
                      comment={comment}
                      busy={busy}
                      onDelete={() => void onDelete(comment)}
                      onMute={() => void onMute(comment)}
                      onInspect={() => setInspecting(comment.userId)}
                    />
                  ))
              )}
            </div>
            <Say
              send={chat.send}
              maxLength={chat.maxLength}
              disabled={!chat.connected}
            />
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {t("chat.notLive")}
          </Text>
        )}

        {/* Shown after the show has ended too, where it is the record of what was done
            (`R-MOD-2`) rather than a set of controls. */}
        {mutes.length > 0 ? (
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              {t("chat.mutedHeading")}
            </Text>
            {mutes.map((one) => {
              const name = one.displayName ?? t("chat.someone");
              return (
                <Group key={one.accountId} justify="space-between" wrap="nowrap">
                  <Text size="sm" truncate="end" title={name}>
                    <button
                      type="button"
                      className={styles.nameButton}
                      aria-label={t("chat.details", { name })}
                      onClick={() => setInspecting(one.accountId)}
                    >
                      {name}
                    </button>
                  </Text>
                  <Button
                    size="compact-xs"
                    variant="default"
                    loading={busy === `unmute:${one.accountId}`}
                    disabled={!live}
                    onClick={() => void onUnmute(one.accountId, name)}
                  >
                    {t("chat.unmute")}
                  </Button>
                </Group>
              );
            })}
          </Stack>
        ) : null}
      </Stack>

      <CustomerDetails
        accountId={inspecting}
        showId={showId}
        onClose={() => {
          setInspecting(undefined);
          // A mute in every show changes nothing in this list, but an unmute in every show may
          // have been pressed by somebody who expects to see the effect — so read it again.
          void load();
        }}
      />
    </Paper>
  );
}

/**
 * One comment, with the two powers spec §13.2 gives a host in their own room.
 *
 * Muting **asks first** and deleting does not, which is a considered asymmetry rather than an
 * inconsistency. A deleted comment costs one message and can be replaced by the sender; a mute
 * silences a customer for the rest of the show and is the kind of thing a moderator does with a
 * phone in one hand during a live broadcast. `R-HOST-25`'s reasoning about ending a show — make
 * the consequential action deliberate — applied at this scale.
 */
function Line({
  comment,
  busy,
  onDelete,
  onMute,
  onInspect,
}: {
  comment: PanelComment;
  busy: string | undefined;
  onDelete: () => void;
  onMute: () => void;
  /** Opens who this commenter is (`R-MOD-1b`) — a display name is not unique. */
  onInspect: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={styles.line}>
      <Text size="sm" className={styles.body}>
        <span className={styles.name}>
          <button
            type="button"
            className={`${styles.nameText} ${styles.nameButton}`}
            title={comment.name || undefined}
            aria-label={t("chat.details", { name: comment.name || t("chat.someone") })}
            onClick={onInspect}
          >
            {comment.name || t("chat.someone")}
          </button>
          {comment.badge ? (
            <Badge size="xs" variant="light" ml={4}>
              {t(`chat.role.${comment.badge}`)}
            </Badge>
          ) : null}
        </span>{" "}
        {comment.content}
      </Text>

      {confirming ? (
        <Group gap={4} wrap="nowrap">
          <Button
            size="compact-xs"
            color="red"
            loading={busy === `mute:${comment.userId}`}
            onClick={() => {
              setConfirming(false);
              onMute();
            }}
          >
            {t("chat.confirmMute")}
          </Button>
          <Button
            size="compact-xs"
            variant="default"
            onClick={() => setConfirming(false)}
          >
            {t("chat.cancel")}
          </Button>
        </Group>
      ) : (
        <Group gap={2} wrap="nowrap" className={styles.actions}>
          <Tooltip label={t("chat.delete")} withArrow>
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              aria-label={t("chat.delete")}
              loading={busy === `delete:${comment.id}`}
              onClick={onDelete}
            >
              <span aria-hidden>{"×"}</span>
            </ActionIcon>
          </Tooltip>
          {/* Never offered against staff. A host muting a moderator, or themselves, is not a
              moderation action — and `R-MOD-1` means the only accounts worth muting are the ones
              who cannot already speak in the panel.

              `role` and **not** `badge`: a member of staff chatting from the room carries no badge
              (only a panel-minted token does — see `chat/api.ts`), and muting them would still be
              the same nonsense. The
              two attributes exist precisely so this line does not follow the badge around. */}
          {comment.role ? null : (
            <Tooltip label={t("chat.mute")} withArrow>
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={t("chat.mute")}
                onClick={() => setConfirming(true)}
              >
                <span aria-hidden>{"⊘"}</span>
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      )}
    </div>
  );
}

/**
 * The host's own composer. One field, because a host talking is not a moderation action.
 *
 * ## It is a form, and it has a send button, because the host may be on a phone
 *
 * Listening for `Enter` as the only way to send anything is fine on the laptop this panel was
 * laid out for and **leaves a host with a phone unable to say a word**: a soft keyboard's return key is not reliably a `keydown` of `Enter` —
 * an IME or an autocorrect suggestion sends `Unidentified` with `keyCode 229` instead — and with
 * nothing else on screen to press, the comment is typed and then stranded. The broadcasting device
 * is a phone (`docs/runbooks/video.md` §2), so the person most likely to be holding one is the host.
 *
 * A real `<form>` fixes the half a button cannot: submitting on the keyboard's action key is
 * then the browser's own behaviour rather than a key name we have to recognise, and
 * `enterKeyHint` makes that key say *send* rather than *return*. The button is the other half,
 * and it is the visible one.
 *
 * Beside the field rather than inside it as `rightSection`, which is where the customer app puts
 * its equivalent. The two surfaces differ on purpose: the room's is a glyph inside a pill, sized
 * for a thumb over a video, and the panel's is a labelled control in a row of labelled controls.
 * A host reading this panel in a hurry gets a word.
 */
function Say({
  send,
  maxLength,
  disabled,
}: {
  send: (content: string) => Promise<boolean>;
  maxLength: number;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const field = useRef<HTMLInputElement | null>(null);

  async function submit() {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setFailed(false);
    const sent = await send(content);
    setSending(false);
    if (sent) {
      setDraft("");
      // The host is typing between other things during a show; keeping focus is the difference
      // between answering three questions and answering one.
      field.current?.focus();
    } else {
      setFailed(true);
    }
  }

  const empty = draft.trim().length === 0;

  return (
    <form
      onSubmit={(event) => {
        // The page must not navigate. Everything this form does, it does in JavaScript.
        event.preventDefault();
        void submit();
      }}
    >
      <Group gap="xs" align="flex-start" wrap="nowrap">
        <TextInput
          ref={field}
          className={styles.grow}
          size="sm"
          placeholder={t("chat.say")}
          value={draft}
          maxLength={maxLength}
          disabled={disabled}
          error={failed ? t("chat.sendFailed") : undefined}
          // Labels the soft keyboard's action key *send*, and on a phone that key is the one a
          // host will reach for first. The form is what makes pressing it actually send.
          enterKeyHint="send"
          onChange={(event) => {
            setDraft(event.currentTarget.value);
            setFailed(false);
          }}
        />
        <Button
          type="submit"
          size="sm"
          loading={sending}
          // Disabled on an empty draft rather than hidden: a control that appears when you start
          // typing is one a host has to discover mid-show. `disabled` is the chat being
          // unreachable, which is the same reason the field is disabled.
          disabled={disabled || empty}
        >
          {t("chat.send")}
        </Button>
      </Group>
    </form>
  );
}
